import { useCallback, useEffect, useRef, useState } from 'react';
import type { Audit, CallRow, Capabilities, CoachingHighlight, Dashboard, Highlight, Job, Rubric, User } from './types';

/**
 * API client.
 *
 * Session lives in an HttpOnly cookie, so `credentials: 'include'` is the whole
 * auth story on this side. No key, token or secret is ever held in the browser.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: { field: string; message: string }[],
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'include',
      ...init,
      headers: init.body instanceof FormData ? (init.headers ?? {}) : { 'content-type': 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check that the API is running.');
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const b = body as { error?: string; issues?: { field: string; message: string }[] } | null;
    throw new ApiError(res.status, b?.error ?? `Request failed (${res.status}).`, b?.issues, body);
  }
  return body as T;
}

export const api = {
  me: () => request<{ user: User }>('/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  rubric: () => request<Rubric>('/rubric'),
  capabilities: () => request<Capabilities>('/capabilities'),
  sdrs: () =>
    request<{ sdrs: { id: string; name: string; team: string | null; coachingHighlightCount: number }[] }>('/sdrs'),

  dashboard: (params: Record<string, string> = {}) =>
    request<Dashboard>(`/dashboard?${new URLSearchParams(params)}`),

  calls: (params: Record<string, string> = {}) =>
    request<{ total: number; limit: number; offset: number; calls: CallRow[] }>(`/calls?${new URLSearchParams(params)}`),

  call: (id: string) =>
    request<{
      call: Record<string, unknown>;
      sdr: { id: string; name: string } | null;
      prospect: Record<string, unknown> | null;
      auditId: string | null;
      job: Job | null;
    }>(`/calls/${id}`),

  audit: (id: string) => request<{ auditId: string; audit: Audit }>(`/calls/${id}/audit`),
  job: (id: string) => request<{ job: Job | null; processingStatus: string; auditId?: string | null }>(`/calls/${id}/job`),
  process: (id: string) => request<{ jobId: string; alreadyRunning?: boolean }>(`/calls/${id}/process`, { method: 'POST' }),
  rawAi: (id: string) =>
    request<{ outputs: { stage: string; provider: string; model: string; response_text: string; latency_ms: number; error: string | null }[] }>(
      `/calls/${id}/raw`,
    ),

  createCall: (form: FormData) => request<{ callId: string; jobId: string | null; transcriptWarnings: string[] }>('/calls', { method: 'POST', body: form }),

  highlight: (callId: string) => request<Highlight>(`/calls/${callId}/highlight`),
  setHighlight: (callId: string, body: { highlighted: boolean; comment: string }) =>
    request<Highlight>(`/calls/${callId}/highlight`, { method: 'PUT', body: JSON.stringify(body) }),
  sdrCoachingHighlights: (sdrId: string) =>
    request<{ sdr: { id: string; name: string }; highlights: CoachingHighlight[] }>(`/sdrs/${sdrId}/coaching-highlights`),
};

/* ------------------------------------------------------------------ *
 * Data hooks
 * ------------------------------------------------------------------ */

export interface QueryState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

export function useQuery<T>(fn: () => Promise<T>, deps: unknown[], enabled = true): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    setError(null);
    fnRef
      .current()
      .then((d) => {
        if (live) setData(d);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof ApiError ? e : new ApiError(0, 'Something went wrong.'));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

/**
 * Poll a processing job until it finishes.
 *
 * Upload -> job -> progress -> completed audit. The browser is never blocked on
 * a multi-stage AI run, and the stage names come from the server so the UI
 * cannot drift out of step with the pipeline.
 */
export function useJobPolling(callId: string | null, active: boolean, intervalMs = 1200) {
  const [job, setJob] = useState<Job | null>(null);
  const [auditId, setAuditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!callId || !active) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const r = await api.job(callId);
        if (!live) return;
        setJob(r.job);
        if (r.auditId) setAuditId(r.auditId);
        if (r.job?.status === 'complete' || r.job?.status === 'failed') return;
        timer = setTimeout(poll, intervalMs);
      } catch (e) {
        if (!live) return;
        setError(e instanceof ApiError ? e.message : 'Lost contact with the server.');
      }
    };
    void poll();

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [callId, active, intervalMs]);

  return { job, auditId, error };
}
